import * as preact from "preact";

import * as accountModal from "accountModal";
import models from "models/models";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as subscriptionPlans from "shared/subscriptionPlans";
import * as signupModal from "signupModal";

type Props = {
  appRouter: any;
};

type State =
  | { type: "not-available" }
  | { type: "loading" }
  | {
      type: "loaded";
      userChats?: {
        _id: string;
        summary: string;
        title: string;
        timestamp: number;
      }[];
    };

class ChatListPageView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { type: "loading" };

    if (
      !languageMap.languageSupportsChat(
        models.user.get("currentLearningLanguage")
      )
    ) {
      this.props.appRouter.navigate("/learn", { trigger: true });
    }

    if (!models.user.hasChatFeature()) {
      this.state = { type: "not-available" };
      // User doesn't have access to the chat feature
      return;
    }

    // This is reduntant while we have the above check, keeping for possible future use
    this.checkSignIn();

    this.fetchChatHistory();

    models.user.on("change:currentLearningLanguage", this.onLanguageChange);
  }

  onLanguageChange = async () => {
    this.setState({ type: "loading" });
    await this.fetchChatHistory();
  };

  async fetchChatHistory() {
    const userChats = (
      await readlangClient.request(
        "GET",
        `/api/chat/history/${models.user.get("currentLearningLanguage")}`,
        {}
      )
    ).data.userChats;

    this.setState({ type: "loaded", userChats });
  }

  async checkSignIn() {
    if (await readlangClient.isLocalMode()) {
      signupModal.show({
        headingHtml: "<h2>Sign in to chat</h2>",
        models: models,
      });
      return;
    }

    if (!models.user.hasPremiumAccess()) {
      accountModal.show({
        model: models.user,
        title: "chat",
        messageHtml: "<h2>Upgrade to chat with Ami...</h2>",
        showTitle: false,
      });
      return;
    }
  }

  upgrade() {
    const isPremium = models.user.hasPremiumAccess();

    accountModal.show({
      model: models.user,
      title: "chatTab",
      messageHtml:
        "<div>" +
        (isPremium
          ? "<h2>Upgrade for unlimited conversations!</h2>"
          : "<h2>Upgrade for conversations!</h2>") +
        "<p>Practice your written conversational skills and more!</p>" +
        "</div>",
      showTitle: false,
      smartDefinitions: true,
    });
  }

  upgradeButton() {
    return (
      <button
        class="upgradeButton prominent"
        onClick={() => {
          if (models.user.hasPremiumPlusAccess()) {
            alert(
              "You've reached the daily limit of " +
                subscriptionPlans.permissions.standard.chat +
                " conversations per day intended to " +
                "prevent abuse of the system. Contact steve@readlang.com if you think this " +
                "has happened in error."
            );
          }
          this.upgrade();
        }}
      >
        Upgrade to Start Chatting
      </button>
    );
  }

  render() {
    const learningLanguageString =
      languageMap.map[models.user.get("currentLearningLanguage")].english;
    const firstLanguageString =
      languageMap.map[models.user.get("firstLanguage")].english;

    if (this.state.type === "not-available") {
      return (
        <div>
          <h2>Upgrade to unlock Conversations!</h2>
          <p>
            Conversations is an exciting new way to practice your written
            conversational skills.
          </p>
          <p>
            Chat with Ami, use a mix of {learningLanguageString} and{" "}
            {firstLanguageString} to avoid getting stuck, get instant feedback
            on your writing, and practice to correct your mistakes.
          </p>
          <h3>Screenshot</h3>
          <img
            class="demoScreenshot"
            src="/content/chatExample.png"
            alt="Chat Example"
          />
          <p>{this.upgradeButton()}</p>
          <h3>Early Feedback</h3>
          <p>
            This is a new feature, and all of the feedback below has been copied
            and pasted from this Readlang forum topic:{" "}
            <a href="https://forum.readlang.com/t/new-conversational-chatbot-for-language-learning/2744/40">
              Conversations Forum Topic
            </a>
            .
          </p>
          <p>
            "I had great time with the tool. Very straight forward. I could talk
            to it through my phone’s voice recognition, which was nice. Extra
            added features are convenient, eg adding words to vocab."
          </p>
          <p>
            "I tried the conversational chatbot briefly and have been really
            impressed. I think this is a welcomed important addition to the
            Readlang toolbox. It really forces an active and immediate effort to
            search for one’s own inner inventory of diction, grammar, syntax and
            so forth. The correction feature is great. Thanks for treating
            Premium users to to new feature and good luck in marshalling it
            through the beta stage!"
          </p>
          <p>
            "This is wonderful! Especially it is wonderful that it offered to
            “save” its suggested corrections as things for me to practice. I
            just checked the “recently added” list…and there they are! As much
            as I previously railed against practicing in “spell the word” mode
            (in favor of something which turned out to be Blitz practice), in
            this case the extra detail is both useful to me and, actually, very
            much appreciated. Most of what tripped me up when confronted with
            the chatbot were the little details os grammar and spelling – not
            knowing the word or phrases I wanted to use."
          </p>
          <p>"Love the feature Steve, thanks!"</p>
          <p>
            "Whoa. Surprising how helpful it is. Also, I didn’t realize Ami
            would remember previous convos. That’s nice."
          </p>
          <p>
            "Tried it, loved it. Will be using it daily – the cards it creates
            from it is just wonderful."
          </p>
          <p>
            "Absolutely love this this new feature, well done! Keep up the good
            work, it is much appreciated!"
          </p>
          <p>
            "This site is even better! Just tried it for the first time with
            Swedish. This is just what I needed. It forces me to actively recall
            words to communicate effectively, and the best thing is I get
            immediate feedback and corrections."
          </p>
          <p>"Gotta say I love it. Extremely useful for me."</p>
          <p>
            "This is fantastic, Steve! Thanks for adding it to the Readlang
            platform, which I’ve been using every day and have been enjoying. I
            love that the chat starts and then it ends. (Other chatbots sort of
            leave you hanging and you don’t get that feeling that you’ve
            completed a conversation. I like that this one has a conclusion at
            each session.) I’ll be using this daily now as well. Bravo!"
          </p>
          <p>"This is really awesome!"</p>
          <p>
            "I’ve been dreaming of exactly this kind of interface since the
            beginning of the LLM trend. I think it’s a perfect use case for it.
            [...] it’s probably my favorite feature in Readlang."
          </p>
          <p>
            "I tried this briefly today (for French) and value it more than
            standard chatbots, given the corrections. It also felt more natural
            in terms of speech patterns for some reason. It’s an extremely
            valuable addition. Thanks!"
          </p>
          <h3>Try it out now!</h3>
          <p>{this.upgradeButton()}</p>
        </div>
      );
    }

    const languageString =
      languageMap.map[models.user.get("currentLearningLanguage")].english;

    /**
     * If set, show "Resume Conversation" button in place of "Start Conversation".
     * This is set if there's a conversation started today that the user can resume.
     */
    const resumeChatID =
      this.state.type === "loaded" && this.state.userChats?.length > 0
        ? (() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayTimestamp = today.getTime();

            // Find the most recent chat from today (userChats is already sorted by most recent first)
            const todaysChat = this.state.userChats.find(
              (chat) => chat.timestamp >= todayTimestamp
            );

            return todaysChat?._id;
          })()
        : undefined;

    return (
      <div>
        <h2>Conversations</h2>
        {this.state.type === "loading" ? (
          <p>Loading...</p>
        ) : (
          <>
            <p class="chatIntro">
              Make friends with Ami, a chatbot who will help you practice your
              conversational {languageString}.
            </p>

            {resumeChatID ? (
              <button
                onClick={() =>
                  this.props.appRouter.navigate(`/chat/${resumeChatID}`, {
                    trigger: true,
                  })
                }
                class="startButton prominent"
              >
                Resume Conversation
              </button>
            ) : (
              <button
                onClick={() =>
                  this.props.appRouter.navigate("/chat/start", {
                    trigger: true,
                  })
                }
                class="startButton prominent"
              >
                Start Conversation
              </button>
            )}
            {this.state.userChats?.length > 0 ? (
              <>
                <h2>Previous Conversations</h2>
                <div class="chatHistory">
                  <table>
                    {this.state.userChats.map((chat) => (
                      <tr>
                        <td>{new Date(chat.timestamp).toLocaleDateString()}</td>
                        <td title={chat.summary}>
                          <a href={`/chat/${chat._id}`}>{chat.title}</a>
                          <i
                            onClick={async () => {
                              if (
                                !confirm(`Are you sure you want to delete this chat?

This action can't be undone.`)
                              ) {
                                return;
                              }

                              // Delete the chat
                              await readlangClient.request(
                                "DELETE",
                                `/api/chat/${chat._id}`,
                                {}
                              );
                              this.fetchChatHistory();
                            }}
                            class="deleteButton icon icon-remove-sign"
                          ></i>
                        </td>
                      </tr>
                    ))}
                  </table>
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    );
  }
}

export default ChatListPageView;
